import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Mail, Phone, ArrowRight, FileText, Database, Settings, Share2, Clock, Lock, UserX, Ban, Scale, RefreshCw, MessageSquare, Edit3, Smartphone } from 'lucide-react';
const sections: any[] = [{
  num: '1',
  icon: Database,
  title: 'What Information Do We Collect?',
  body: ['We collect personal information that you voluntarily provide: Names, Email addresses, and Phone numbers. We also automatically collect technical information such as IP address, browser type, device data, and location data when you visit our Services.'],
  callout: {
    label: 'Sensitive Information',
    text: 'We do not process sensitive information.'
  }
}, {
  num: '2',
  icon: Settings,
  title: 'How Do We Process Your Information?',
  body: ['We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.']
}, {
  num: '3',
  icon: Share2,
  title: 'When and With Whom Do We Share Your Personal Information?',
  bullets: [{
    label: 'Business Transfers:',
    text: 'In connection with any merger, sale of company assets, or acquisition.'
  }, {
    label: 'Business Partners:',
    text: 'To offer you certain products, services, or promotions.'
  }]
}, {
  num: '4',
  icon: Clock,
  title: 'How Long Do We Keep Your Information?',
  body: ['We keep your personal information only as long as necessary for the purposes outlined in this privacy notice. When no longer needed, we will delete or anonymize such information.']
}, {
  num: '5',
  icon: Lock,
  title: 'How Do We Keep Your Information Safe?',
  body: ['We have implemented appropriate technical and organizational security measures. However, no electronic transmission over the internet can be guaranteed to be 100% secure.']
}, {
  num: '6',
  icon: UserX,
  title: 'Do We Collect Information From Minors?',
  body: [<>
        We do not knowingly collect data from children under 18 years of age. By
        using our Services, you represent that you are at least 18. Contact us
        at{' '}
        <a href="mailto:info@ics-claims.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-798" data-mp="qt9crcf9p8m0">
          info@ics-claims.com
        </a>{' '}
        if you believe we have collected data from a minor.
      </>]
}, {
  num: '7',
  icon: Ban,
  title: 'Controls For Do-Not-Track Features',
  body: ['We do not currently respond to DNT browser signals. California law requires us to inform you that because there is no industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at this time.']
}, {
  num: '8',
  icon: Scale,
  title: 'Do United States Residents Have Specific Privacy Rights?',
  body: ["California residents may have the right to request access to, correct, or delete their personal information. We have not disclosed, sold, or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months outside of service providers, payors, or vendors of an active workers' compensation claim."],
  bullets: [{
    label: 'Your Rights:',
    text: 'Right to access, correct, delete, and obtain a copy of your data; right to non-discrimination; right to opt out of targeted advertising.'
  }, {
    label: 'To Exercise Your Rights:',
    textNode: <>
            Visit{' '}
            <Link to="/contact" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-799" data-mp="11ee719351h5b">
              arissacs.com/contact
            </Link>
            , email{' '}
            <a href="mailto:info@ArissaCS.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-800" data-mp="tdf682jnzuge">
              info@ArissaCS.com
            </a>
            , or call{' '}
            <a href="tel:8552601053" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-801" data-mp="fhup9cnhxmbm">
              855-260-1053
            </a>
            .
          </>
  }]
}, {
  num: '9',
  icon: RefreshCw,
  title: 'Do We Make Updates to This Notice?',
  body: ['Yes, we will update this notice as necessary to stay compliant with relevant laws. The updated version will be indicated by an updated "Last Updated" date at the top of this notice, and the updated version will be effective as soon as it is accessible.']
}, {
  num: '10',
  icon: MessageSquare,
  title: 'How Can You Contact Us About This Notice?',
  body: [<>
        If you have questions or comments about this notice, you may email us at{' '}
        <a href="mailto:info@ArissaCS.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-802" data-mp="2hb7kznzhcepu">
          info@ArissaCS.com
        </a>{' '}
        or call us at{' '}
        <a href="tel:8552601053" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-803" data-mp="1thxtg4pefj4p">
          855-260-1053
        </a>
        .
      </>]
}, {
  num: '11',
  icon: Edit3,
  title: 'How Can You Review, Update, or Delete the Data We Collect From You?',
  body: [<>
        Based on the applicable laws of your country or state of residence in
        the U.S., you may have the right to request access to the personal
        information we collect from you, details about how we have processed it,
        correct inaccuracies, or delete your personal information. To request to
        review, update, or delete your personal information, please{' '}
        <Link to="/contact" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-804" data-mp="21jna3apg87fd">
          contact us
        </Link>
        .
      </>]
}, {
  num: '12',
  icon: Smartphone,
  title: 'Will You Use My Cell Phone Number For Texting?',
  body: [<span key="subheading" className="block font-bold text-brand-navy text-lg" data-id="element-805" data-mp="1irm4lpi65iwz">
        Text Messaging Communications
      </span>, 'We may use text messaging (SMS) to communicate with our customers regarding various matters related to our products, services, promotions, events, and important updates. By providing your mobile phone number and opting in to receive text messages from us, you consent to using text messaging as a means of communication.', <span key="types" className="block font-bold text-brand-navy pt-2" data-id="element-806" data-mp="3kha29udupvdl">
        Types of Messages:
      </span>],
  bullets: [{
    label: 'Service Updates:',
    text: 'Text messages regarding updates to our services, including maintenance notifications or policy changes.'
  }, {
    label: 'Transactional Messages:',
    text: 'Text messages sent for transactional purposes such as appointment reminders or confirmations.'
  }, {
    label: 'Promotional Messages:',
    text: 'Promotional text messages about our products, special offers, discounts, or upcoming events.'
  }, {
    label: 'Message Frequency:',
    text: 'Message frequency may vary depending on your interactions with us and the preferences you have set. We strive to keep our communications relevant and valuable to you.'
  }, {
    label: 'Opting Out:',
    text: 'You can opt out of receiving text messages from us at any time by following the instructions in the text message or contacting us directly. Opting out may impact your ability to receive important updates or offers from us.'
  }, {
    label: 'Data Usage and Charges:',
    text: 'Standard message and data rates may apply depending on your mobile carrier and plan. We are not responsible for any charges incurred from receiving text messages from us.'
  }, {
    label: 'Data Protection:',
    text: 'Any information collected through text messaging is subject to our Privacy Policy.'
  }],
  footer: 'By providing your mobile number and opting in to receive text messages from us, you acknowledge that you have read and understood our text messaging policies outlined in this Privacy Notice.'
}];
export function Privacy() {
  return <div className="bg-brand-paper" data-id="element-807" data-mp="x9unt1ouy2ys">
      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden" data-id="element-808" data-mp="127w7nou3y6bb">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90)'
      }} data-id="element-809" data-mp="12aiaxc9ytznq"></div>
        <div className="absolute inset-0 bg-brand-navy/92 mix-blend-multiply" data-id="element-810" data-mp="1g6m4r4gxg5zo"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/60" data-id="element-811" data-mp="1l0882617axit"></div>
        <div className="absolute inset-0 opacity-10" data-id="element-812" data-mp="1pxgnvzyvmpgw">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-813" data-mp="1u03mqktytanl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-814" data-mp="1rjv4w5rtogh9">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-815" data-mp="1whwm0l5ou5q3">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6" data-id="element-816" data-mp="2a801mta2b16k">
              <Shield className="w-3.5 h-3.5 text-brand-gold" data-id="element-817" data-mp="2f9fnuonz7dqj" />
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-818" data-mp="2k80mijnkxpcv">
                Privacy Notice
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-819" data-mp="pt7vkxxn5mr9">
              Your <span className="text-brand-gold italic" data-id="element-820" data-mp="uskhnggoupss">privacy</span>{' '}
              matters.
            </h1>
            <p className="text-xl text-white/85 leading-relaxed font-light max-w-2xl" data-id="element-821" data-mp="383c4m6h1ypba">
              This privacy notice for Arissa Cost Solutions, Inc. describes how
              and why we might collect, store, use, and/or share your
              information when you use our services, such as when you visit our
              website at arissacs.com or engage with us in other related ways.
            </p>
            <p className="mt-6 text-sm text-brand-gold font-semibold tracking-widest uppercase" data-id="element-822" data-mp="3qysrx29e50ys">
              Last Updated: June 05, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick contact callout */}
      <section className="bg-white -mt-12 relative z-10" data-id="element-823" data-mp="3d1nf8jf693uk">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-824" data-mp="3d388m5pr2gqu">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-id="element-825" data-mp="26lux1tifw0ad">
            <div className="w-14 h-14 rounded-xl bg-brand-gold/10 text-brand-gold flex items-center justify-center flex-shrink-0" data-id="element-826" data-mp="1z0hmvr7bzip9">
              <FileText className="w-7 h-7" data-id="element-827" data-mp="1kbbk5t9abacm" />
            </div>
            <div className="flex-1" data-id="element-828" data-mp="1p4eisfsx59zo">
              <h3 className="font-serif font-bold text-xl text-brand-navy mb-1" data-id="element-829" data-mp="1p5fv0nlo82iz">
                Questions or concerns?
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-830" data-mp="1fd1qxo74ejtn">
                Contact us at{' '}
                <a href="mailto:info@ArissaCS.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-831" data-mp="11e22ohnis6y0">
                  info@ArissaCS.com
                </a>
                .
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-20 bg-brand-paper" data-id="element-832" data-mp="wqaprd74vd7z">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6" data-id="element-833" data-mp="wmwo676an81l">
          {sections.map((section, i) => {
          const Icon = section.icon;
          return <motion.div key={section.num} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: Math.min(i * 0.03, 0.2)
          }} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100" data-id="element-834" data-mp="15o4muqcq2aw7" data-mpi={i}>
                <div className="flex items-start gap-5 mb-6" data-id="element-835" data-mp="3dvq5zy7qtraz">
                  <div className="hidden sm:flex w-12 h-12 rounded-lg bg-brand-navy text-brand-gold items-center justify-center flex-shrink-0" data-id="element-836" data-mp="3iv2u0macp4f5">
                    <Icon className="w-6 h-6" data-id="element-837" data-mp="3smgxt69c4qe3" />
                  </div>
                  <div className="flex-1" data-id="element-838" data-mp="3slnsm9q0y8y2">
                    <div className="text-brand-gold font-bold tracking-widest text-xs uppercase mb-2" data-id="element-839" data-mp="2uz87mxye2t5r">
                      Section {section.num}
                    </div>
                    <h3 className="font-serif font-bold text-2xl md:text-3xl text-brand-navy leading-tight" data-id="element-840" data-mp="2zzmg2h3wk54b">
                      {section.title}
                    </h3>
                  </div>
                </div>

                <div className="space-y-4 sm:pl-[68px]" data-id="element-841" data-mp="k6x8ujfp5u4u">
                  {section.body?.map((p: any, idx: number) => <p key={idx} className="text-gray-700 leading-relaxed" data-id="element-842" data-mp="o77flybbcokr">
                      {p}
                    </p>)}

                  {section.callout && <div className="bg-brand-paper border-l-4 border-brand-gold rounded-r-lg p-4" data-id="element-843" data-mp="24ofwf17lhmg9">
                      <span className="font-bold text-brand-navy" data-id="element-844" data-mp="1zx69479iqv7j">
                        {section.callout.label}:
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-845" data-mp="1uwsbzk9gh5oa">
                        {section.callout.text}
                      </span>
                    </div>}

                  {section.bullets && <ul className="space-y-3 pt-2" data-id="element-846" data-mp="1gz3pk7zqpqh4">
                      {section.bullets.map((b: any, idx: number) => <li key={idx} className="flex items-start" data-id="element-847" data-mp="1c0k0hypunykj" data-mpi={idx}>
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 mr-4 flex-shrink-0" data-id="element-848" data-mp="1c2dbfq7hx4yc"></span>
                          <span className="text-gray-700 leading-relaxed" data-id="element-849" data-mp="36eba4y86iifz">
                            <span className="font-bold text-brand-navy" data-id="element-850" data-mp="36c76mhe6zlg8">
                              {b.label}
                            </span>{' '}
                            {b.textNode || b.text}
                          </span>
                        </li>)}
                    </ul>}

                  {section.footer && <p className="text-gray-700 leading-relaxed pt-3" data-id="element-851" data-mp="3lhl0db5nfafq">
                      {section.footer}
                    </p>}
                </div>
              </motion.div>;
        })}
        </div>
      </section>

      {/* Contact callout */}
      <section className="py-20 bg-white" data-id="element-852" data-mp="320t95pvo43v">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-853" data-mp="35g5m0s62z1l">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-brand-navy rounded-2xl p-10 md:p-12 text-white relative overflow-hidden" data-id="element-854" data-mp="80lqqxdqw9om">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-brand-gold/15 rounded-full blur-3xl" data-id="element-855" data-mp="2u9kurp2p3n8p"></div>
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-856" data-mp="2paq5q5e4x2g5"></div>

            <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center" data-id="element-857" data-mp="21kvrpki2x7ub">
              <div data-id="element-858" data-mp="21it6ddrvg2vx">
                <h3 className="font-serif text-3xl md:text-4xl text-white mb-4 leading-tight" data-id="element-859" data-mp="1xjage3l711m8">
                  Need to exercise your rights?
                </h3>
                <p className="text-white/80 leading-relaxed" data-id="element-860" data-mp="2bc05whzxkoo3">
                  Reach out anytime to access, update, or delete your
                  information — or to ask any privacy-related question.
                </p>
              </div>
              <div className="space-y-4" data-id="element-861" data-mp="1dvkweiji05nk">
                <a href="mailto:info@ArissaCS.com" className="flex items-center bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-5 py-4 hover:bg-white/15 transition-colors" data-id="element-862" data-mp="1dus1cetwj4l9">
                  <Mail className="w-5 h-5 text-brand-gold mr-4 flex-shrink-0" data-id="element-863" data-mp="12a60c33r7t7z" />
                  <div data-id="element-864" data-mp="12c8wl4vi2il5">
                    <div className="text-xs uppercase tracking-wider text-white/60 font-semibold" data-id="element-865" data-mp="178zg626ijwbb">
                      Email
                    </div>
                    <div className="text-white" data-id="element-866" data-mp="1c7u4fjn92d2t">info@ArissaCS.com</div>
                  </div>
                </a>
                <a href="tel:8552601053" className="flex items-center bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-5 py-4 hover:bg-white/15 transition-colors" data-id="element-867" data-mp="1g7ukt9twsyci">
                  <Phone className="w-5 h-5 text-brand-gold mr-4 flex-shrink-0" data-id="element-868" data-mp="1uz3dxsx0t1ue" />
                  <div data-id="element-869" data-mp="1zxxyp0ejt0h5">
                    <div className="text-xs uppercase tracking-wider text-white/60 font-semibold" data-id="element-870" data-mp="1zxeo64r89ed5">
                      Phone
                    </div>
                    <div className="text-white" data-id="element-871" data-mp="3kdw0agy76i6b">855-260-1053</div>
                  </div>
                </a>
                <Link to="/contact" className="flex items-center justify-between bg-brand-gold text-brand-navy rounded-xl px-5 py-4 hover:bg-white transition-colors font-semibold" data-id="element-872" data-mp="3fj8lxawwr45g">
                  Contact our team
                  <ArrowRight className="w-5 h-5" data-id="element-873" data-mp="70j7v8un560h" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}